import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { publishReplay, refCount, scan, startWith, switchMap } from 'rxjs/operators';
import { Settings } from '../core';
import { EQueryCounter } from './models/e-query-counter';
import { find } from 'lodash';

const enum MutationType {
    Add,
    Remove
}

interface AddMutation {
    readonly type: MutationType;
    readonly key: number;
    readonly eQueryId: number;
}

interface RemoveMutation {
    readonly type: MutationType;
    readonly key: number;
    readonly eQueryId: number;
}

type Mutation =
    | AddMutation
    | RemoveMutation
    ;


interface UpdateCountersReduceResult {
    unresolved: number[];
    resolved: number[];
}

export abstract class EQueriesCountersStorage {
    private readonly currentCounters$ = new BehaviorSubject<Observable<EQueryCounter[]>>(this.loadAllCounters());
    private readonly mutations$ = new Subject<Mutation[]>();
    private readonly counters$ = this.currentCounters$.pipe(
        switchMap(currentCounters => this.calculateCounters(currentCounters)),
        publishReplay(1),
        refCount(),
    );

    public get counters(): Observable<EQueryCounter[] | null> {
        return this.counters$;
    }

    constructor(
        protected readonly settings: Settings,
        protected readonly http: HttpClient,
    ) { }

    public dispose() {
        this.currentCounters$.complete();
    }

    public refreshAllCounters(): Observable<EQueryCounter[] | null> {
        this.currentCounters$.next(this.loadAllCounters());
        return this.counters$;
    }

    public decreaseCounters(key: number, eQueryIds: number[]) {
        this.mutations$.next(this.mapMutations(MutationType.Remove, key, eQueryIds));
    }

    public increaseCounters(key: number, eQueryIds: number[]) {
        this.mutations$.next(this.mapMutations(MutationType.Add, key, eQueryIds));
    }

    public updateCounters<T>(
        key: number,
        eQueries: T[],
        eQueryIdFn: (eQuery: T) => number,
        unresolvedFn: (entity: T) => boolean,
    ) {
        const eQueryIds = eQueries.reduce<UpdateCountersReduceResult>(
            (result, eq) => {
                const eQueryId = eQueryIdFn(eq);
                if (unresolvedFn(eq)) {
                    result.unresolved.push(eQueryId);
                } else {
                    result.resolved.push(eQueryId);
                }
                return result;
            },
            { unresolved: [], resolved: [] });

        this.increaseCounters(
            key,
            eQueryIds.unresolved
        );
        this.decreaseCounters(
            key,
            eQueryIds.resolved
        );
    }

    protected abstract loadAllCounters(): Observable<EQueryCounter[]>;

    // IE: The Set ctor doesn't support passing an initial array, so use forEach + add for filling
    protected createSet(initArray: number[] = []): Set<number> {
        const s = new Set<number>();
        initArray.forEach(x => s.add(x));
        return s;
    }

    private mapMutations(type: MutationType, key: number, eQueryIds: number[]): Mutation[] {
        return eQueryIds.map<Mutation>(eQueryId => ({ type, key, eQueryId }));
    }

    private createQueuedMutations(): Observable<Mutation[]> {
        const stack: Mutation[][] = [];
        let next: ((items: Mutation[] | undefined) => void) | null = null;
        const subscription = this.mutations$.subscribe(x => {
            if (next != null) {
                next(x);
            } else {
                stack.push(x);
            }
        });
        return new Observable<Mutation[]>(subscriber => {
            next = x => subscriber.next(x);
            while (stack.length > 0) {
                const item = stack.shift();
                next(item);
            }
            return () => {
                subscription.unsubscribe();
                next = null;
            };
        });
    }

    private calculateCounters(currentCounters$: Observable<EQueryCounter[]>): Observable<EQueryCounter[] | null> {
        const mutations$ = this.createQueuedMutations();
        return currentCounters$.pipe(
            switchMap(x =>
                mutations$.pipe(
                    startWith([]),
                    scan(this.mutateCounters, x),
                    publishReplay(1),
                    refCount(),
                ),
            ),
            startWith(null)
        );
    }

    private mutateCounters = (counters: EQueryCounter[], mutations: Mutation[]): EQueryCounter[] => {
        for (const mutation of mutations) {
            switch (mutation.type) {
                case MutationType.Add: {
                    this.addCounter(counters, mutation);
                    break;
                }
                case MutationType.Remove: {
                    this.removeCounter(counters, mutation);
                    break;
                }
                default:
                    continue;
            }
        }
        return counters;
    }

    private addCounter(counters: EQueryCounter[], mutation: Mutation) {
        const counter = find(counters, c => c.key === mutation.key);
        if (counter != null) {
            counter.totalUnresolved.add(mutation.eQueryId);
        } else {
            counters.push({
                key: mutation.key,
                total: 1,
                totalUnresolved: this.createSet([mutation.eQueryId]),
            });
        }
    }

    private removeCounter(counters: EQueryCounter[], mutation: Mutation) {
        const counter = find(counters, c => c.key === mutation.key);
        if (counter != null) {
            counter.totalUnresolved.delete(mutation.eQueryId);
        }
    }
}
